import React, { useEffect, useMemo, useState } from 'react';
import api, { extrairMensagemErro, storageKeys } from '../services/api';
import { storage } from '../services/storage';
import { perfilPode, obterUsuarioSessao } from '../permissions';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';

type ItemCompra = {
  nome?: string;
  descricao?: string;
  descricao_tecnica?: string;
  quantidade?: number | string;
  unidade?: string;
  observacoes?: string;
};

type EventoOperacional = {
  id: string;
  acao: string;
  valores_depois?: string | null;
  data_criacao?: string;
  usuario?: { nome: string; email: string; perfil: string };
};
type ProvaInput = { nome: string; mime: string; conteudo: string };
type EventoArmazem = { id: string; tipo: string; data_hora?: string; descricao?: string; resultado?: string; responsavel?: { nome: string }; prova_visual?: { disponivel: boolean; url?: string; nome?: string } };

type RequisicaoArmazem = {
  id: string;
  numero_requisicao: string;
  titulo: string;
  descricao?: string;
  departamento?: string;
  itens?: ItemCompra[];
  valor_estimado: number;
  valor_pago_ext?: number;
  valor_final_contratado?: number;
  moeda: string;
  status: string;
  status_aprovacao: string;
  nome_fornecedor_ext?: string;
  fornecedor_selecionado?: string | null;
  tipo_processo?: string;
  data_compra?: string;
  data_entrada_transito?: string;
  data_criacao?: string;
  comprovativo_nome?: string;
  comprovativo_compra?: { nome?: string; tipo?: string; validado_em?: string } | null;
  comprador_responsavel?: { nome: string; email: string; perfil: string } | null;
  criador?: { nome: string; email: string; perfil?: string };
  proposta_vencedora?: {
    valor_proposto: number;
    moeda?: string;
    fornecedor?: { nome_empresa: string; email: string };
    comprovativo_nome?: string;
  };
  propostas?: Array<{ id: string; valor_proposto: number; status: string }>;
  historico_operacional?: EventoOperacional[];
  historico_armazem?: EventoArmazem[];
};

type FiltrosArmazem = {
  numero: string;
  fornecedor: string;
  solicitante: string;
  status: string;
  data_inicio: string;
  data_fim: string;
};

const labelsStatus: Record<string, string> = {
  COMPRA_REALIZADA: 'Em Trânsito',
  EM_TRANSITO: 'Em Trânsito',
  CONCLUIDO: 'Concluída'
};

const labelsAcao: Record<string, string> = {
  CRIAR_REQUISICAO: 'Criação da requisição',
  REQUISICAO_APROVAR: 'Aprovação',
  REQUISICAO_REJEITAR: 'Rejeição',
  SELECIONAR_PROPOSTA_VENCEDORA: 'Seleção da proposta',
  FECHAR_COMPRA_DIRETA: 'Conclusão da compra direta',
  VALIDAR_COMPROVATIVO_COMPRA: 'Conclusão da compra',
  ENCAMINHAR_ARMAZEM: 'Entrada em trânsito',
  RECEBER_MERCADORIA: 'Recebimento',
  REGISTRAR_OCORRENCIA_ENTREGA: 'Ocorrência na entrega'
};

const moeda = (valor: number | undefined, codigo = 'KZ') =>
  `${codigo} ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;

const dataCurta = (data?: string) => {
  if (!data) return 'Sem data';
  const valor = new Date(data);
  if (Number.isNaN(valor.getTime())) return 'Sem data';
  return new Intl.DateTimeFormat('pt-AO').format(valor);
};

const dataHora = (data?: string) => {
  if (!data) return 'Sem data';
  const valor = new Date(data);
  if (Number.isNaN(valor.getTime())) return 'Sem data';
  return new Intl.DateTimeFormat('pt-AO', { dateStyle: 'short', timeStyle: 'short' }).format(valor);
};

const extrairObservacao = (evento: EventoOperacional) => {
  if (!evento.valores_depois) return '';
  try {
    const valores = JSON.parse(evento.valores_depois);
    return valores.descricao || valores.observacao || valores.observacoes || valores.comentario || valores.status || valores.estado_resultante || '';
  } catch {
    return '';
  }
};

const valorCompra = (req: RequisicaoArmazem) =>
  req.valor_final_contratado ||
  req.valor_pago_ext ||
  req.proposta_vencedora?.valor_proposto ||
  req.valor_estimado;

const estaEmTransito = (req: RequisicaoArmazem) => ['COMPRA_REALIZADA', 'EM_TRANSITO'].includes(req.status);

const lerProva = (ficheiro: File): Promise<ProvaInput> => new Promise((resolve, reject) => {
  const leitor = new FileReader();
  leitor.onload = () => resolve({ nome: ficheiro.name, mime: ficheiro.type, conteudo: String(leitor.result || '') });
  leitor.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
  leitor.readAsDataURL(ficheiro);
});
export const resolverChaveIdempotencia = (chaveAtual?: string) => chaveAtual || globalThis.crypto.randomUUID();

const ProvaVisual: React.FC<{ evento: EventoArmazem }> = ({ evento }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(Boolean(evento.prova_visual?.disponivel));
  useEffect(() => {
    let objectUrl: string | null = null;
    let isMounted = true;
    if (!evento.prova_visual?.disponivel || !evento.prova_visual.url) return;
    api.get(evento.prova_visual.url, { responseType: 'blob' })
      .then((resposta) => {
        if (!isMounted) return;
        objectUrl = URL.createObjectURL(resposta.data);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (isMounted) setErro(true);
      })
      .finally(() => {
        if (isMounted) setCarregando(false);
      });
    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [evento.prova_visual?.disponivel, evento.prova_visual?.url]);
  if (!evento.prova_visual?.disponivel) return <small>Registo antigo sem prova visual.</small>;
  if (carregando) return <small role="status">A carregar prova visual…</small>;
  if (erro || !url) return <small role="alert">Não foi possível carregar a prova visual.</small>;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={`Prova visual: ${evento.prova_visual.nome || evento.tipo}`} style={{ maxWidth: 180, maxHeight: 120, objectFit: 'cover' }} /></a>;
};

const Armazem: React.FC = () => {
  const [requisicoes, setRequisicoes] = useState<RequisicaoArmazem[]>([]);
  const [observacoes, setObservacoes] = useState<Record<string, string>>({});
  const [ocorrencias, setOcorrencias] = useState<Record<string, string>>({});
  const [provasRecebimento, setProvasRecebimento] = useState<Record<string, File | undefined>>({});
  const [provasOcorrencia, setProvasOcorrencia] = useState<Record<string, File | undefined>>({});
  const [chavesOcorrencia, setChavesOcorrencia] = useState<Record<string, string | undefined>>({});
  const [recebendoId, setRecebendoId] = useState<string | null>(null);
  const [ocorrenciaId, setOcorrenciaId] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<RequisicaoArmazem | null>(null);
  const [dashboardLogistica, setDashboardLogistica] = useState<any>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);
  const [filtros, setFiltros] = useState<FiltrosArmazem>({
    numero: '',
    fornecedor: '',
    solicitante: '',
    status: '',
    data_inicio: '',
    data_fim: ''
  });

  const usuario = useMemo(() => obterUsuarioSessao(), []);

  const podeReceber = perfilPode(usuario?.perfil, 'armazem.receber');

  const carregar = async (pagina = paginacao.pagina) => {
    try {
      setCarregando(true);
      setErro(null);
      const params = Object.fromEntries(Object.entries(filtros).filter(([, valor]) => Boolean(valor)));
      const paramsDashboard: Record<string, string> = {};
      if (filtros.data_inicio) paramsDashboard.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) paramsDashboard.data_fim = filtros.data_fim;
      const [resposta, painel] = await Promise.all([
        api.get('/requisicoes', { params: { ...params, pagina, limite: paginacao.limite } }),
        api.get('/dashboard/bi', { params: paramsDashboard })
      ]);
      const dados = resposta.data.dados || [];
      setPaginacao(resposta.data.paginacao || paginacaoVazia);
      setRequisicoes(
        dados.filter((req: RequisicaoArmazem) =>
          ['COMPRA_REALIZADA', 'EM_TRANSITO', 'CONCLUIDO'].includes(req.status)
        )
      );
      setDashboardLogistica(painel.data.indicadores?.logistica || null);
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar recebimentos do armazém.'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmarRecebimento = async () => {
    if (!confirmacao || !podeReceber || !estaEmTransito(confirmacao)) return;

    try {
      setRecebendoId(confirmacao.id);
      setErro(null);
      setMensagem(null);

      const ficheiro = provasRecebimento[confirmacao.id];
      const resposta = await api.post(`/requisicoes/${confirmacao.id}/receber-mercadoria`, {
        observacoes: observacoes[confirmacao.id] || undefined,
        prova_visual: ficheiro ? await lerProva(ficheiro) : undefined
      });

      setMensagem(resposta.data.mensagem || 'Mercadoria recebida com sucesso.');
      setObservacoes((atuais) => ({ ...atuais, [confirmacao.id]: '' }));
      setProvasRecebimento((atuais) => ({ ...atuais, [confirmacao.id]: undefined }));
      setConfirmacao(null);
      await carregar();
    } catch (erroRecebimento: any) {
      setErro(extrairMensagemErro(erroRecebimento, 'Erro ao confirmar recebimento da mercadoria.'));
    } finally {
      setRecebendoId(null);
    }
  };

  const registarOcorrencia = async (requisicao: RequisicaoArmazem) => {
    const observacao = (ocorrencias[requisicao.id] || '').trim();
    const ficheiro = provasOcorrencia[requisicao.id];
    if (!podeReceber || !estaEmTransito(requisicao) || !observacao || !ficheiro) {
      setErro('Informe uma descrição e selecione uma imagem para registar a ocorrência.');
      return;
    }

    try {
      setOcorrenciaId(requisicao.id);
      setErro(null);
      setMensagem(null);
      const chave = resolverChaveIdempotencia(chavesOcorrencia[requisicao.id]);
      setChavesOcorrencia((atuais) => ({ ...atuais, [requisicao.id]: chave }));
      const resposta = await api.post(
        `/requisicoes/${requisicao.id}/registrar-ocorrencia-entrega`,
        { observacao, prova_visual: await lerProva(ficheiro) },
        { headers: { 'Idempotency-Key': chave } }
      );
      setMensagem(resposta.data.mensagem || 'Ocorrência registada.');
      setOcorrencias((atuais) => ({ ...atuais, [requisicao.id]: '' }));
      setProvasOcorrencia((atuais) => ({ ...atuais, [requisicao.id]: undefined }));
      setChavesOcorrencia((atuais) => ({ ...atuais, [requisicao.id]: undefined }));
      await carregar();
    } catch (erroOcorrencia: any) {
      setErro(extrairMensagemErro(erroOcorrencia, 'Erro ao registar ocorrência de entrega.'));
    } finally {
      setOcorrenciaId(null);
    }
  };

  return (
    <div className="workspace-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Armazém</span>
          <h1>Recebimento de mercadoria</h1>
          <p>Conferência física, rastreabilidade logística e encerramento seguro do ciclo.</p>
        </div>
      </header>

      {!podeReceber && (
        <div className="alerta alerta-aviso">
          A operação de recebimento está disponível apenas para utilizadores autorizados pelo armazém.
        </div>
      )}
      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}

      <section className="metric-grid warehouse-metrics">
        <div className="metric-card"><span>Em trânsito</span><strong>{dashboardLogistica?.em_transito || 0}</strong></div>
        <div className="metric-card"><span>Aguardando recepção</span><strong>{dashboardLogistica?.aguardando_recepcao || 0}</strong></div>
        <div className="metric-card"><span>Recebidas</span><strong>{dashboardLogistica?.recebidas || 0}</strong></div>
        <div className="metric-card"><span>Ocorrências abertas</span><strong>{dashboardLogistica?.ocorrencias_abertas || 0}</strong></div>
        <div className="metric-card"><span>Recebimentos concluídos</span><strong>{dashboardLogistica?.recebimentos_concluidos || 0}</strong></div>
      </section>

      <section className="panel warehouse-filter-panel">
        <div className="filters-grid warehouse-filters">
          <label>Número<input value={filtros.numero} onChange={(e) => setFiltros({ ...filtros, numero: e.target.value })} placeholder="REQ-..." /></label>
          <label>Fornecedor<input value={filtros.fornecedor} onChange={(e) => setFiltros({ ...filtros, fornecedor: e.target.value })} /></label>
          <label>Solicitante<input value={filtros.solicitante} onChange={(e) => setFiltros({ ...filtros, solicitante: e.target.value })} /></label>
          <label>
            Estado
            <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}>
              <option value="">Todos</option>
              <option value="EM_TRANSITO">Em Trânsito</option>
              <option value="CONCLUIDO">Concluída</option>
            </select>
          </label>
          <label>Início<input type="date" value={filtros.data_inicio} onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })} /></label>
          <label>Fim<input type="date" value={filtros.data_fim} onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })} /></label>
          <button className="botao botao-primario" type="button" onClick={() => carregar(1)}>Filtrar</button>
        </div>
      </section>

      {carregando ? (
        <div className="inline-loading"><div className="spinner"></div><span>Carregando recebimentos...</span></div>
      ) : (
        <section className="card-list warehouse-card-list">
          {requisicoes.map((req) => {
            const emTransito = estaEmTransito(req);
            const eventos = [
              {
                id: `${req.id}-criacao`,
                acao: 'Criação da requisição',
                data: req.data_criacao,
                utilizador: req.criador?.nome || 'Solicitante',
                detalhe: req.descricao || ''
              },
              ...(req.historico_operacional || []).map((evento) => ({
                id: evento.id,
                acao: labelsAcao[evento.acao] || evento.acao.replace(/_/g, ' '),
                data: evento.data_criacao,
                utilizador: evento.usuario?.nome || 'Sistema',
                detalhe: extrairObservacao(evento)
              }))
            ].sort((a, b) => new Date(a.data || 0).getTime() - new Date(b.data || 0).getTime());

            return (
              <article className={`warehouse-card warehouse-card-expanded ${emTransito ? 'warehouse-card-priority' : ''}`} key={req.id}>
                <div className="warehouse-card-main">
                  <div className="warehouse-card-header">
                    <span className={`status-pill ${emTransito ? 'status-transit' : 'status-done'}`}>
                      {labelsStatus[req.status] || req.status}
                    </span>
                    <span className="status-pill muted">{req.numero_requisicao}</span>
                    <span className="status-pill muted">{req.tipo_processo === 'COMPRA_DIRETA' ? 'Compra direta' : 'Proposta'}</span>
                  </div>

                  <h2>{req.titulo}</h2>
                  {req.descricao && <p>{req.descricao}</p>}

                  <dl className="meta-grid warehouse-detail-grid">
                    <div><dt>Número</dt><dd>{req.numero_requisicao}</dd></div>
                    <div><dt>Solicitante</dt><dd>{req.criador?.nome || 'Não informado'}</dd></div>
                    <div><dt>Departamento / centro de custo</dt><dd>{req.departamento || 'Não informado'}</dd></div>
                    <div><dt>Comprador responsável</dt><dd>{req.comprador_responsavel?.nome || 'Não informado'}</dd></div>
                    <div><dt>Fornecedor selecionado</dt><dd>{req.fornecedor_selecionado || 'Não informado'}</dd></div>
                    <div><dt>Data da compra</dt><dd>{dataCurta(req.data_compra)}</dd></div>
                    <div><dt>Valor final contratado</dt><dd>{moeda(valorCompra(req), req.moeda)}</dd></div>
                    <div><dt>Comprovativo / fatura</dt><dd>{req.comprovativo_compra?.nome || req.comprovativo_nome || req.proposta_vencedora?.comprovativo_nome || 'Não anexado'}</dd></div>
                  </dl>

                  <section className="warehouse-subsection">
                    <h3>Itens comprados para conferência</h3>
                    <div className="warehouse-items-grid">
                      {(Array.isArray(req.itens) ? req.itens : []).map((item, indice) => (
                        <div className="warehouse-item" key={`${req.id}-item-${indice}`}>
                          <strong>{item.nome || item.descricao || `Item ${indice + 1}`}</strong>
                          <span>{item.descricao_tecnica || item.descricao || 'Sem descrição técnica'}</span>
                          <small>{item.quantidade || 0} {item.unidade || 'un.'}</small>
                          {item.observacoes && <small>Obs.: {item.observacoes}</small>}
                        </div>
                      ))}
                      {(!Array.isArray(req.itens) || req.itens.length === 0) && (
                        <div className="empty-state compact-state">Itens estruturados não registados.</div>
                      )}
                    </div>
                  </section>

                  <section className="warehouse-subsection">
                    <h3>Histórico operacional</h3>
                    <div className="history-list warehouse-history">
                      {eventos.map((evento) => (
                        <div className="history-item" key={evento.id}>
                          <time>{dataHora(evento.data)}</time>
                          <div>
                            <strong>{evento.acao}</strong>
                            <small>{evento.utilizador}</small>
                            {evento.detalhe && <p>{evento.detalhe}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                  {(req.historico_armazem || []).length > 0 && (
                    <section className="warehouse-subsection">
                      <h3>Recebimentos e ocorrências</h3>
                      <div className="history-list warehouse-history">
                        {(req.historico_armazem || []).map((evento) => <div className="history-item" key={evento.id}>
                          <time>{dataHora(evento.data_hora)}</time><div><strong>{evento.tipo}</strong>
                            <small>{evento.responsavel?.nome || 'Armazém'} · {evento.resultado || 'Sem resultado'}</small>
                            <p>{evento.descricao || 'Sem descrição.'}</p><ProvaVisual evento={evento} />
                          </div>
                        </div>)}
                      </div>
                    </section>
                  )}
                </div>

                <div className="receive-panel">
                  {emTransito ? (
                    <>
                      <h3>Encerrar logística</h3>
                      <textarea
                        placeholder="Observações do recebimento"
                        value={observacoes[req.id] || ''}
                        onChange={(e) => setObservacoes({ ...observacoes, [req.id]: e.target.value })}
                        disabled={!podeReceber || recebendoId === req.id}
                      />
                      <label>Prova visual opcional
                        <input type="file" accept="image/jpeg,image/png,image/webp"
                          disabled={!podeReceber || recebendoId === req.id}
                          onChange={(e) => setProvasRecebimento({ ...provasRecebimento, [req.id]: e.target.files?.[0] })} />
                      </label>
                      <button
                        className="botao botao-primario"
                        type="button"
                        disabled={!podeReceber || recebendoId === req.id}
                        onClick={() => setConfirmacao(req)}
                      >
                        {recebendoId === req.id ? 'Confirmando...' : 'Confirmar recebimento'}
                      </button>

                      <div className="exception-box">
                        <h3>Ocorrência na entrega</h3>
                        <textarea
                          placeholder="Obrigatório quando houver divergência, avaria ou falta"
                          value={ocorrencias[req.id] || ''}
                          onChange={(e) => {
                            setOcorrencias({ ...ocorrencias, [req.id]: e.target.value });
                            setChavesOcorrencia({ ...chavesOcorrencia, [req.id]: undefined });
                          }}
                          disabled={!podeReceber || ocorrenciaId === req.id}
                        />
                        <label>Prova visual obrigatória
                          <input type="file" required accept="image/jpeg,image/png,image/webp"
                            disabled={!podeReceber || ocorrenciaId === req.id}
                            onChange={(e) => {
                              setProvasOcorrencia({ ...provasOcorrencia, [req.id]: e.target.files?.[0] });
                              setChavesOcorrencia({ ...chavesOcorrencia, [req.id]: undefined });
                            }} />
                        </label>
                        <button
                          className="botao botao-secundario"
                          type="button"
                          disabled={!podeReceber || ocorrenciaId === req.id}
                          onClick={() => registarOcorrencia(req)}
                        >
                          {ocorrenciaId === req.id ? 'Registando...' : 'Registar ocorrência'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="empty-state compact-state">Recebimento já concluído. Alterações operacionais bloqueadas.</div>
                  )}
                </div>
              </article>
            );
          })}

          {requisicoes.length === 0 && (
            <div className="empty-state">
              Nenhuma compra em trânsito ou concluída encontrada para os filtros atuais.
            </div>
          )}
          <Paginacao paginacao={paginacao} carregando={carregando} aoMudarPagina={carregar} />
        </section>
      )}

      {confirmacao && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-conteudo confirmation-modal">
            <h2>Confirmar recebimento</h2>
            <p>
              Esta operação encerrará a etapa logística da requisição {confirmacao.numero_requisicao}.
              Após concluída, não será possível realizar novo recebimento nem alterações operacionais.
            </p>
            <div className="button-row">
              <button className="botao botao-secundario" type="button" onClick={() => setConfirmacao(null)}>Cancelar</button>
              <button className="botao botao-primario" type="button" onClick={confirmarRecebimento}>
                Confirmo o encerramento logístico
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Armazem;
